import { subject } from '@casl/ability'
import {
  type Home,
  type IncidentReportUpdateBody,
  type LogUpdateBody,
  UserRole
} from '@lmc/ts-common'
import {
  Button,
  Check,
  EmptyState,
  Form,
  FormCard,
  NoDataIcon,
  PageHeader,
  Tag,
  Trash
} from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import useIncidentTypeOptions from 'hooks/useIncidentTypeOptions'
import useLog from 'hooks/useLog/useLog'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import {
  HeaderChildrenContainer,
  IconContainer,
  LogTitle,
  TitleContainer,
  Wrapper
} from './components/display/commonStyledComponents'
import IncidentLogFormFields from './components/forms/IncidentLogFormFields'
import IncidentTypeChangeModal from './components/modals/IncidentTypeChangeModal'
import RemoveIncidentModal from './components/modals/RemoveIncidentModal'
import { tagColorMap } from './utils'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

type IncidentLogEditScreenProps = {
  testId?: string
}

const IncidentLogEditScreen = ({
  testId = 'incident-edit'
}: IncidentLogEditScreenProps = {}) => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const {
    log: incidentLog,
    isLogLoading,
    updateLog,
    removeLog,
    isRemoveLoading
  } = useLog({
    logId: incidentLogId,
    onUpdateSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.LOG_UPDATED)
      })
      navigate(`../`, { relative: 'path' })
    },
    onRemoveSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.LOG_REMOVED)
      })
      navigate(`../`, { relative: 'path' })
    }
  })

  const hasRolePermission = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', { log: { item: incidentLog?.item } })
    }
  ])

  const handleOpen = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const { incidentReport, isIncidentReportLoading } = useIncidentReportByLog({
    incidentLogId
  })

  const { goals } = useGoals(incidentLog?.resident as unknown as string)

  const incidentTypes = useIncidentTypeOptions(INCIDENT_CATEGORY_ITEM)

  if (isLogLoading || isIncidentReportLoading)
    return <Spinner data-testid={`${testId}-spinner`} />
  if (!incidentLog || !incidentReport)
    return (
      <EmptyState
        data-testid={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues: Partial<IncidentReportUpdateBody> = {
    ...incidentLog,
    dateLogged: dayjs(incidentLog.timeLogged),
    timeLogged: dayjs(incidentLog.timeLogged),
    item: incidentReport.log.item?.id
  }

  const onSave = async ({ item, ...values }: LogUpdateBodyExtended) => {
    try {
      const datePart = dayjs(values.dateLogged)
      const timePart = dayjs(values.timeLogged)
      const formattedDate = datePart
        .hour(timePart.hour())
        .minute(timePart.minute())
        .second(timePart.second())
        .millisecond(timePart.millisecond())

      setIsUpdateLoading(true)
      setIsDirty(false)
      const hasLogItemProperty = !!item && Object.keys(item).length > 0

      await updateLog({
        ...values,
        ...(hasLogItemProperty && { item }),
        timeLogged: formattedDate
      })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsUpdateLoading(false)
  }

  const handleOnUpdateFinish = (values: LogUpdateBodyExtended) => {
    if (values.item !== incidentLog.item) {
      handleOpen()
    } else {
      onSave(values)
    }
  }

  const handleRemoveLog = async () => {
    try {
      await removeLog()
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
  }

  const handleFormValuesChange = (
    values: Partial<IncidentReportUpdateBody>
  ) => {
    if (values) {
      setIsDirty(true)
    }
  }

  return (
    <>
      <PageHeader
        testId={testId}
        sticky
        onBack={() => navigate('../', { relative: 'path' })}
        title={
          <TitleContainer data-testid={`${testId}-title-container`}>
            <IconContainer
              data-testid={`${testId}-icon`}
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            {incidentLog.title}
            <Tag
              data-testid={`${testId}-status-tag`}
              color={tagColorMap[incidentReport.status]}
            >
              {t(incidentReportsConstants[incidentReport.status])}
            </Tag>
          </TitleContainer>
        }
      >
        <HeaderChildrenContainer data-testid={`${testId}-header-actions`}>
          <Button
            data-testid={`${testId}-remove-button`}
            variant="danger"
            onClick={() => setIsRemoveModalOpen(true)}
            disabledByPermission={!hasRolePermission}
          >
            <Trash />
            {t(incidentReportsConstants.REMOVE)}
          </Button>
          <Button
            data-testid={`${testId}-cancel-button`}
            onClick={() => navigate('../', { relative: 'path' })}
          >
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            data-testid={`${testId}-save-button`}
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper data-testid={`${testId}-wrapper`}>
        <Form
          data-testid={`${testId}-form`}
          form={form}
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            errorFields.map((errorField) =>
              errorField.errors.map((err) =>
                showToast({ title: err, type: 'error' })
              )
            )
          }
        >
          <FormCard
            testId={`${testId}-form-card`}
            title={
              <LogTitle level={3} data-testid={`${testId}-log-title`}>
                {t(incidentReportsConstants.LOG)}
              </LogTitle>
            }
            subtitle={`${t(incidentReportsConstants.LOGGED_BY)} ${
              incidentReport.log.loggedBy?.name || ''
            }`}
          >
            <IncidentLogFormFields
              testId={`${testId}-form-fields`}
              goals={goals}
              locations={home.locations}
              incidentTypes={incidentTypes}
              // @ts-ignore
              hasLocationsEnabled={home?.features?.hasHomeLocations?.enabled}
              item={incidentReport.log.item}
            />
          </FormCard>
        </Form>
        <NavigationModal canNavigate={!isDirty} />
        <RemoveIncidentModal
          data-testid={`${testId}-remove-modal`}
          open={isRemoveModalOpen}
          onConfirm={handleRemoveLog}
          onCancel={() => setIsRemoveModalOpen(false)}
          isRemoveLoading={isRemoveLoading}
        />
        <IncidentTypeChangeModal
          data-testid={`${testId}-type-change-modal`}
          open={isModalVisible}
          handleCancel={handleCancel}
          onConfirm={() => onSave(form.getFieldsValue())}
        />
      </Wrapper>
    </>
  )
}

interface LogUpdateBodyExtended extends Partial<LogUpdateBody> {
  dateLogged: string
}

export default IncidentLogEditScreen
